import * as React from 'react';
import { Link, RouteComponentProps } from 'react-router-dom';
import * as moment from 'moment-timezone';
import { fetchApi } from '../../services/api';
import { SortDescriptor, orderBy, filterBy, CompositeFilterDescriptor, FilterDescriptor } from '@progress/kendo-data-query';
import ChildAutoComplete, { ChildSearchResult } from '../ChildAutoComplete';
import ClassroomComboBox, { ClassroomSearchResult } from '../ClassroomComboBox';
import LoadingPanel from '../LoadingPanel';
import { saveAs } from 'file-saver';
import {
    Grid,
    GridColumn as Column,
    GridSortChangeEvent,
    GridToolbar,
    GridPageChangeEvent,
    GridCell
} from '@progress/kendo-react-grid';
import AccountAutoComplete, { AccountSearchResult } from '../AccountAutoComplete';
import FacilityComboBox, { FacilitySearchResult } from '../FacilityComboBox';
import InstructorComboBox, { InstructorSearchResult } from '../InstructorComboBox';
const DocumentTitle = require('react-document-title');

export interface AttendanceData {
    ScheduleId: number;
    ChildId: number;
    Name: string;
    AccountId: number;
    AccountName: string;
    Link: string;
    CheckInTime: DateTimeValue | null;
    CheckOutTime: DateTimeValue | null;
    FacilityId: number;
    FacilityIANA: string;
    FacilityName: string;
    ClassroomName: string;
    ClassroomId: number;
    RowId: number; //Used for zero/empty records
    InstructorId: number | null;
    InstructorName: string | null;
    [key: string]: any;
}

export type DateTimeValue = {
    DateTime: Date;
    IANA: string;
    DateTimeString: string;
}

type Props = RouteComponentProps<{}>;

type State = {
    skip: number;
    take: number;
    pageSize: number;
    pageable: any;
    filter: CompositeFilterDescriptor;
    sort: SortDescriptor[];
    attendances: AttendanceData[];
    filteredAttendances: AttendanceData[];
    isLoading: boolean;
    searchFromDate: string;
    searchToDate: string;
    searchChild: ChildSearchResult | null;
    searchAccount: AccountSearchResult | null;
    searchFacility: FacilitySearchResult | null;
    searchClassroom: ClassroomSearchResult | null;
    searchInstructor: InstructorSearchResult | null;
    localIANA: string;
}

const centerDiv = {
    margin: "auto",
    width: "50%",
    textAlign: "center",
    opacity: 0.4,
    fontSize: "14px",
    fontStyle: "italic"
} as React.CSSProperties;

const labelPaddingRight = {
    paddingRight: "10px"
} as React.CSSProperties;

const formPaddingLeft = {
    paddingRight: "10px"
} as React.CSSProperties;

class AttendanceReport extends React.Component<Props, State> {

    private dateTimeout!: NodeJS.Timeout;

    constructor(props: Props) {
        super(props);

        this.state = {
            filter: { logic: 'and', filters: [] = [] } as CompositeFilterDescriptor,
            skip: 0,
            take: 50,
            pageSize: 50,
            pageable: this.state ? this.state.pageable : {
                buttonCount: 10,
                info: true,
                type: 'numeric',
                pageSizes: [50, 100, 150],
                previousNext: true
            },
            sort: [],
            attendances: [],
            filteredAttendances: [],
            isLoading: false,
            searchFromDate: moment().format("YYYY-MM-DD").toString(),
            searchToDate: moment().format("YYYY-MM-DD").toString(),
            searchChild: null,
            searchAccount: null,
            searchFacility: null,
            searchClassroom: null,
            searchInstructor: null,
            localIANA: moment.tz.guess()
        }
        this.sortChange = this.sortChange.bind(this);
        this.pageChange = this.pageChange.bind(this);
        this.setDate = this.setDate.bind(this);
        this.download = this.download.bind(this);
        this.dataStateChange = this.dataStateChange.bind(this);
    }

    public componentWillMount() {
        this.getReport();
    }

    public componentDidMount() {
        this.drawGridFooter();
    }

    private removeGridFooterLabels() {
        var myNode = document.getElementById('grid-footer-hdr-totals');
        while (myNode.firstChild) {
            myNode.removeChild(myNode.firstChild);
        }
    }

    private insertGridFooterLabel(html: string) {
        var node = document.createElement('label');
        node.innerHTML = html;
        document.getElementById('grid-footer-hdr-totals').insertAdjacentElement('beforeend', node);
    }

    private drawGridFooter() {
        var div = document.createElement('div');
        div.innerHTML = "<div class='k-pager-info k-label' id='grid-footer-hdr-totals' style='display:grid'></div>";
        div.id = "grid-footer-hdr";
        div.className = "k-pager-wrap k-grid-pager k-widget";
        this.insertAfter(div, document.getElementsByClassName('k-pager-wrap k-grid-pager k-widget')[0]);
    }

    private insertAfter(el: any, referenceNode: any) {
        referenceNode.parentNode.insertBefore(el, referenceNode.nextSibling);
    }

    private pageChange(event: GridPageChangeEvent) {
        this.setState({
            skip: event.page.skip,
            take: event.page.take,
            pageSize: event.page.take
        });
    }

    public render() {
        if (!this.state.attendances) {
            return <React.Fragment>
                <div style={centerDiv}>Loading...</div>
            </React.Fragment>
        }

        let data: AttendanceData[] = this.state.attendances;

        data = filterBy(data, this.state.filter);

        return <React.Fragment>
            <DocumentTitle title="Schedules" />
            <div className="row">
                <div className="col-1">
                    <h1>Schedules</h1>
                </div>
                <div className="col-11 text-right">
                    <button className="btn btn-primary" onClick={() => this.props.history.push('/reports/calendarattendance')} style={{ margin: "0 10px 0 0" }}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="feather feather-calendar">
                            <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                            <line x1="16" y1="2" x2="16" y2="6" />
                            <line x1="8" y1="2" x2="8" y2="6" />
                            <line x1="3" y1="10" x2="21" y2="10" />
                        </svg>
                        &nbsp;Calendar View
                    </button>
                    <button className="btn btn-primary float-right" disabled={this.state.attendances.length === 0} onClick={(e) => { this.download(e) }}>Download/Print</button>
                </div>
            </div>
            {this.state.isLoading && <LoadingPanel />}
                <Grid
                    style={{ maxHeight: "1000px" }}
                    skip={this.state.skip}
                    take={this.state.take}
                    total={data.length}
                    pageable={this.state.pageable}
                    pageSize={this.state.pageSize}
                    onPageChange={this.pageChange}
                    sortable={{ allowUnsort: true }}
                    sort={this.state.sort}
                    onSortChange={this.sortChange}
                    filter={this.state.filter}
                    data={data.slice(this.state.skip, this.state.take + this.state.skip)}
                >
                    <GridToolbar>
                        <div className="row">
                            <div className="col-11">
                                <div className="form-inline">
                                    <div className="form-group" style={formPaddingLeft}>
                                        <label style={labelPaddingRight}>From:</label>
                                        <input className="form-control" type="date" defaultValue={this.state.searchFromDate} id="fromDate" onChange={(e) => { this.setDate(e) }} style={{ width: "175px" }} />
                                    </div>
                                    <div className="form-group" style={formPaddingLeft}>
                                        <label style={labelPaddingRight}>To:</label>
                                        <input className="form-control" type="date" defaultValue={this.state.searchToDate} id="toDate" onChange={(e) => { this.setDate(e) }} style={{ width: "175px" }} />
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="row" style={{ paddingTop: "10px" }}>
                            <div className="col-12">
                                <div className="form-inline">
                                    <div className="form-group" style={formPaddingLeft}>
                                        <ChildAutoComplete
                                            className="form-control"
                                            style={{ color: '#007bff', width: "100%" }}
                                            classroomId={null}
                                            callbackFromParent={this.myCallback.bind(this)} />
                                    </div>
                                    <div className="form-group" style={formPaddingLeft}>
                                        <ClassroomComboBox
                                            style={{ color: '#007bff', width: "100%" }}
                                            callbackFromParent={this.myClassroomCBCallback.bind(this)} />
                                    </div>
                                    <div className="form-group" style={formPaddingLeft}>
                                        <FacilityComboBox
                                            style={{ color: '#007bff', width: "100%" }}
                                            callbackFromParent={this.myFacilityCBCallback.bind(this)} />
                                    </div>
                                    <div className="form-group" style={formPaddingLeft}>
                                        <InstructorComboBox
                                            style={{ color: '#007bff', width: "100%" }}
                                            callbackFromParent={this.myInstructorCBCallback.bind(this)} />
                                    </div>
                                    <div className="form-group" style={formPaddingLeft}>
                                        <AccountAutoComplete
                                            className="form-control"
                                            style={{ color: '#007bff', width: "100%", marginTop: "5px" }}
                                            callbackFromParent={this.myAccountACCallback.bind(this)} />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </GridToolbar>
                    <Column field="Name" title="Child" cell={ChildNameCell} />
                    <Column field="CheckInTime.DateTime" title="In" cell={CheckInCell} filter="date" />
                    <Column field="CheckOutTime.DateTime" title="Out" cell={CheckOutCell} filter="date" />
                    <Column field="InstructorName" title="Instructor" cell={InstructorNameCell} />
                    <Column field="FacilityName" title="Facility" cell={FacilityNameCell} />
                </Grid>
            {!this.state.isLoading && this.dataStateChange(data)}
        </React.Fragment>;
    }

    myCallback = (result: ChildSearchResult) => {
        this.setState({ searchChild: result });
        if (result)
            this.filterVia('ChildId', result.ChildId);
        else
            this.filterVia('ChildId', null);
    };

    myAccountACCallback = (result: AccountSearchResult) => {
        this.setState({ searchAccount: result });
        if (result)
            this.filterVia('AccountId', result.AccountId);
        else
            this.filterVia('AccountId', null);
    };

    myClassroomCBCallback = (result: ClassroomSearchResult) => {
        this.setState({ searchClassroom: result });
        if (result)
            this.filterVia('ClassroomId', result.ClassroomId);
        else
            this.filterVia('ClassroomId', null);
    };

    myFacilityCBCallback = (result: FacilitySearchResult) => {
        this.setState({ searchFacility: result });
        if (result)
            this.filterVia('FacilityId', result.FacilityId);
        else
            this.filterVia('FacilityId', null);
    };

    myInstructorCBCallback = (result: InstructorSearchResult) => {
        this.setState({ searchInstructor: result });
        if (result)
            this.filterVia('InstructorId', result.InstructorId);
        else
            this.filterVia('InstructorId', null);
    };

    private setDate(event: React.ChangeEvent<HTMLInputElement>) {
        if (moment(event.target.value).isValid()) {
            if (event.target.id === 'fromDate') {
                this.setState({ searchFromDate: event.target.value });
            } else {
                this.setState({ searchToDate: event.target.value });
            }
            clearTimeout(this.dateTimeout);
            this.dateTimeout = setTimeout(() => {
                this.getReport();
            }, 1000);
        }
    }

    private filterVia(field: string, value: number|null) {
        let myCompositeFilter: CompositeFilterDescriptor = this.state.filter;

        let myFilterDescriptors = myCompositeFilter.filters as FilterDescriptor[];
        let myFilterDescriptor = myFilterDescriptors.find(x => x.field === field);

        if (myFilterDescriptor) {
            if (value)
                myFilterDescriptor.value = value;
            else
                myFilterDescriptors = myFilterDescriptors.filter(x => x.field !== field);
        } else if (value) {
            myFilterDescriptors.push({ field: field, operator: "eq", value: value });
        }

        myCompositeFilter.filters = myFilterDescriptors;

        this.setState({
            filter: myCompositeFilter,
            skip: 0
        });
    }

    private sortChange(e: GridSortChangeEvent) {
        this.setState({
            sort: e.sort,
            attendances: orderBy(this.state.attendances, e.sort)
        });
    }

    private download(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) {
        const data = {
            FacilityID: this.state.searchFacility != null ? this.state.searchFacility.FacilityId : null,
            AttendanceItems: filterBy(this.state.attendances, this.state.filter),
            FromDate: this.state.searchFromDate,
            ToDate: this.state.searchToDate,
        }
        this.setState({ isLoading: true });
        fetchApi('/api/Reports/DownloadAttendanceReport', data, 'POST')
            .then((blob: Blob) => {
                var newBlob = new Blob([blob], { type: 'application/pdf' });
                saveAs(newBlob, "attendance.pdf");
                this.setState({ isLoading: false });
            }).catch(err => {
                this.setState({ isLoading: false });
            });
    }

    private getReport() {
        const data = {
            FromDate: this.state.searchFromDate,
            ToDate: this.state.searchToDate,
            LocalIANA: this.state.localIANA
        }
        this.setState({ isLoading: true });
        fetchApi('/api/Reports/AttendanceSearch', data, 'POST')
            .then((attendances: AttendanceData[]) => {
                this.setState({ attendances, isLoading: false }, () => { });
            });
    }

    private dataStateChange(data: AttendanceData[]) {
        let totalHours: number = 0;
        for (const key in data) {
            let value = data[key];

            if (value.CheckInTime && value.CheckOutTime) {
                totalHours += moment.duration(moment(value.CheckOutTime.DateTime).diff(moment(value.CheckInTime.DateTime))).asMinutes();
            }
        }
        this.removeGridFooterLabels();
        this.insertGridFooterLabel("Total Hours: " + (totalHours / 60).toFixed(2));
    }
}

class FacilityNameCell extends GridCell {
    render() {
        return (
            <td>
                <Link style={{ color: '#007bff' }} to={`/facility/${this.props.dataItem.FacilityId}`}>
                    {this.props.dataItem.FacilityName}
                </Link>
            </td>
        );
    }
}

class ChildNameCell extends GridCell {
    render() {
        return (
            <td>
                <Link style={{ color: '#007bff' }} to={`/child/${this.props.dataItem.ChildId}`}>
                    {this.props.dataItem.Name}
                </Link>
            </td>
        );
    }
}

class CheckInCell extends GridCell {
    render() {
        if (this.props.dataItem.CheckInTime && this.props.dataItem.CheckInTime.DateTime) {
            return (
                <td>{moment.utc(this.props.dataItem.CheckInTime.DateTime).tz(this.props.dataItem.CheckInTime.IANA).format("MM/DD/YYYY hh:mm A z")}</td>
            );
        } else {
            return (
                <td>N/A</td>
            );
        }
    }
}

class CheckOutCell extends GridCell {
    render() {
        if (this.props.dataItem.CheckOutTime && this.props.dataItem.CheckOutTime.DateTime) {
            return (
                <td>{moment.utc(this.props.dataItem.CheckOutTime.DateTime).tz(this.props.dataItem.CheckOutTime.IANA).format("MM/DD/YYYY hh:mm A z")}</td>
            );
        } else {
            return (
                <td>N/A</td>
            );
        }
    }
}

class InstructorNameCell extends GridCell {
    render() {
        if (this.props.dataItem.InstructorId == 0) {
            return (
                <td>None</td>
            );
        } else {
            return (
                <td>{this.props.dataItem.InstructorName}</td>
            );
        }
    }
}

export default AttendanceReport;
